import type { Code, ListItem, Root } from "mdast";
import remarkParse from "remark-parse";
import remarkStringify from "remark-stringify";
import { unified } from "unified";
import { visit } from "unist-util-visit";

/**
 * Set of valid HTML element names (lowercase).
 * Tags not in this set will be escaped to prevent markdown parser issues.
 */
const VALID_HTML_ELEMENTS = new Set([
  // Main root
  "html",
  // Document metadata
  "base", "head", "link", "meta", "style", "title",
  // Sectioning root
  "body",
  // Content sectioning
  "address", "article", "aside", "footer", "header", "h1", "h2", "h3", "h4", "h5", "h6", "hgroup", "main", "nav", "section", "search",
  // Text content
  "blockquote", "dd", "div", "dl", "dt", "figcaption", "figure", "hr", "li", "menu", "ol", "p", "pre", "ul",
  // Inline text semantics
  "a", "abbr", "b", "bdi", "bdo", "br", "cite", "code", "data", "dfn", "em", "i", "kbd", "mark", "q", "rp", "rt", "ruby", "s", "samp", "small", "span", "strong", "sub", "sup", "time", "u", "var", "wbr",
  // Image and multimedia
  "area", "audio", "img", "map", "track", "video",
  // Embedded content
  "embed", "iframe", "object", "param", "picture", "portal", "source",
  // SVG and MathML
  "svg", "math",
  // Scripting
  "canvas", "noscript", "script",
  // Demarcating edits
  "del", "ins",
  // Table content
  "caption", "col", "colgroup", "table", "tbody", "td", "tfoot", "th", "thead", "tr",
  // Forms
  "button", "datalist", "fieldset", "form", "input", "label", "legend", "meter", "optgroup", "option", "output", "progress", "select", "textarea",
  // Interactive elements
  "details", "dialog", "summary",
  // Web Components
  "slot", "template",
]);

/**
 * Removes HTML comments from content.
 * Comments like <!-- hide this --> will be stripped entirely.
 * Exported for reuse in other markdown processing utilities.
 */
export function stripHtmlComments(content: string): string {
  return content.replace(/<!--[\s\S]*?-->/g, "");
}

/**
 * Escapes non-standard HTML tags to prevent the markdown parser from treating them as raw HTML.
 * Tags like <thinking>, <example>, <context> etc. will be escaped to &lt;tagname&gt;.
 */
function escapeNonHtmlTags(content: string): string {
  // Match opening tags: <tagname> or <tagname attr="value">
  // Match closing tags: </tagname>
  // Match self-closing tags: <tagname/>
  const tagPattern = /<\/?([a-zA-Z][a-zA-Z0-9_-]*)\s*[^>]*\/?>/g;

  return content.replace(tagPattern, (match, tagName) => {
    const lowerTagName = tagName.toLowerCase();
    if (VALID_HTML_ELEMENTS.has(lowerTagName)) {
      return match; // Keep valid HTML tags as-is
    }
    // Escape the entire tag
    return match.replace(/</g, "&lt;").replace(/>/g, "&gt;");
  });
}

/**
 * Remark plugin to fix code block indentation in list items.
 * Ensures code blocks in lists have proper 4-space indentation for correct rendering.
 */
function remarkFixCodeBlockIndentation() {
  return (tree: Root) => {
    visit(tree, "listItem", (listItem: ListItem) => {
      visit(listItem, "code", (codeNode: Code) => {
        // The mdast parser already handles the proper structure,
        // but we can ensure the stringified output has correct indentation
        // by setting the indent property if it exists
        if ("indent" in codeNode) {
          // Set proper indentation: first line no indent, subsequent lines 4 spaces
          (codeNode as any).indent = [0, 4, 4, 4];
        }
      });
    });
  };
}

/**
 * Preprocesses markdown content to fix common formatting issues.
 * Currently handles:
 * - Escaping non-standard HTML tags (e.g., <thinking>, <example>) so they render as text
 * - Code block indentation in list items
 */
export async function preprocessMarkdown(content: string): Promise<string> {
  try {
    // Strip HTML comments before any other processing
    const withoutComments = stripHtmlComments(content);
    // Escape non-HTML tags before parsing to ensure they're treated as text
    const escapedContent = escapeNonHtmlTags(withoutComments);

    const processor = unified()
      .use(remarkParse)
      .use(remarkFixCodeBlockIndentation)
      .use(remarkStringify, {
        listItemIndent: "one",
        bullet: "-",
        fences: true,
        emphasis: "*",
        strong: "*",
        rule: "-",
        handlers: {
          // Prevent escaping of backticks in inline code
          inlineCode: (node: any) => `\`${node.value}\``
        }
      });

    const result = await processor.process(escapedContent);
    const stringified = String(result);
    return restoreBareUrlUnderscores(stringified);
  } catch (error) {
    // If preprocessing fails, return the original content
    console.warn("Markdown preprocessing failed:", error);
    return content;
  }
}

/**
 * remark-stringify escapes underscores in bare URLs which then renders the literal `\_` sequence.
 * We undo that escaping so markdown renders clean URLs again without sacrificing other escapes.
 */
function restoreBareUrlUnderscores(markdown: string): string {
  const bareUrlPattern = /https?:\/\/[^\s<>()[\]]+/g;

  return markdown.replace(bareUrlPattern, url => url.replace(/\\_/g, "_"));
}
